/**
 * Corridor and intent fields on the company record (WP1.5).
 *
 * Option lists (value + label) come from `GET /api/v1/companies/intentOptions/`
 * — the corridor sides are backend configuration, so nothing here hard-codes
 * Quebec / GCC. Target countries / regions reuse `regionOptions.ts`.
 */
import i18n from "../i18n";

export interface CompanyIntentValue {
  corridorSide: string;
  tradeRoles: string[];
  companySize: string;
  objective: string;
  objectiveDetails: string;
  targetCountries: string[];
  targetRegions: string[];
  marketEntryTimeline: string;
}

export type CompanyIntentErrors = Partial<Record<keyof CompanyIntentValue, string>>;

export const INTENT_FIELD_KEYS: Array<keyof CompanyIntentValue> = [
  "corridorSide",
  "tradeRoles",
  "companySize",
  "objective",
  "objectiveDetails",
  "targetCountries",
  "targetRegions",
  "marketEntryTimeline",
];

export const EMPTY_INTENT: CompanyIntentValue = {
  corridorSide: "",
  tradeRoles: [],
  companySize: "",
  objective: "",
  objectiveDetails: "",
  targetCountries: [],
  targetRegions: [],
  marketEntryTimeline: "",
};

export const OBJECTIVE_OTHER = "other";
const OBJECTIVE_DETAILS_MAX = 500;

/** Read-side shape: the generated `Company` type marks the lists readonly. */
export type CompanyIntentReadable = {
  readonly [K in keyof CompanyIntentValue]?: CompanyIntentValue[K] extends string[]
    ? readonly string[]
    : CompanyIntentValue[K];
};

/** Build the form value from a company read payload (missing → empty). */
export function intentFromCompany(company: CompanyIntentReadable | null | undefined): CompanyIntentValue {
  if (!company) return { ...EMPTY_INTENT };
  return {
    corridorSide: company.corridorSide ?? "",
    tradeRoles: [...(company.tradeRoles ?? [])],
    companySize: company.companySize ?? "",
    objective: company.objective ?? "",
    objectiveDetails: company.objectiveDetails ?? "",
    targetCountries: [...(company.targetCountries ?? [])],
    targetRegions: [...(company.targetRegions ?? [])],
    marketEntryTimeline: company.marketEntryTimeline ?? "",
  };
}

/**
 * Client-side checks mirroring the backend. `required` is true on
 * registration (side, roles, size, objective and timeline are mandatory there).
 */
export function validateIntent(value: CompanyIntentValue, { required }: { required: boolean }): CompanyIntentErrors {
  const errors: CompanyIntentErrors = {};
  if (required) {
    if (!value.corridorSide) errors.corridorSide = i18n.t("domain:intent.errors.corridorSide");
    if (value.tradeRoles.length === 0) errors.tradeRoles = i18n.t("domain:intent.errors.tradeRoles");
    if (!value.companySize) errors.companySize = i18n.t("domain:intent.errors.companySize");
    if (!value.objective) errors.objective = i18n.t("domain:intent.errors.objective");
    if (!value.marketEntryTimeline) errors.marketEntryTimeline = i18n.t("domain:intent.errors.marketEntryTimeline");
  }
  if (value.objective === OBJECTIVE_OTHER && !value.objectiveDetails.trim()) {
    errors.objectiveDetails = i18n.t("domain:intent.errors.objectiveDetails");
  }
  if (value.objectiveDetails.length > OBJECTIVE_DETAILS_MAX) {
    errors.objectiveDetails = i18n.t("domain:intent.errors.objectiveDetailsTooLong", { max: OBJECTIVE_DETAILS_MAX });
  }
  return errors;
}

/** Trim free text before sending. */
export function intentPayload(value: CompanyIntentValue): CompanyIntentValue {
  return { ...value, objectiveDetails: value.objectiveDetails.trim() };
}

/** Pick the corridor/intent keys out of a field-error map from the API. */
export function pickIntentErrors(fieldErrors: Record<string, string | undefined>): CompanyIntentErrors {
  const out: CompanyIntentErrors = {};
  for (const key of INTENT_FIELD_KEYS) {
    if (fieldErrors[key]) out[key] = fieldErrors[key];
  }
  return out;
}

export interface IntentOption {
  value: string;
  label: string;
}

/** The generated option items have optional fields; drop malformed rows. */
export function toOptions(items: Array<{ value?: string; label?: string }> | undefined): IntentOption[] {
  return (items ?? [])
    .filter((o): o is { value: string; label?: string } => Boolean(o.value))
    .map((o) => ({ value: o.value, label: o.label || o.value }));
}

export function optionLabel(options: IntentOption[], value: string | undefined | null): string {
  if (!value) return "";
  return options.find((o) => o.value === value)?.label ?? value;
}
